import React, { useEffect, useMemo, useState } from 'react';
import { HelpIcon } from '@qualtrics/ui-react/icons';
import { HelpIcon as PortalHelpIcon } from '../icons';
import IconButton from '../IconButton/IconButton';
import { getTier1DataTestId } from '../../Service/testing';
import { messageService } from '../../Service/message-service';
import { useScreenType } from '../hooks/useScreenType';
import { usePortalsInfo } from '../../Context';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} HelpProps
 * @property {import(react).Element=} customIcon
 */

/**
 * @type {import(react).FC<Help>}
 */
export default function Help({ customIcon }) {
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);
  const { isMobileView } = useScreenType();
  const { isPortalsApplication } = usePortalsInfo();

  const helpModalId = useMemo(() => {
    return isPortalsApplication
      ? 'end-user-portal-view-help-modal'
      : 'support-modal-header';
  }, [isPortalsApplication]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsHelpModalVisible(!!document.getElementById(helpModalId));
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    return () => observer.disconnect();
  }, [helpModalId]);

  const helpIconComponent = useMemo(() => {
    const CustomIcon = customIcon;
    if (isPortalsApplication) {
      return CustomIcon ? (
        <CustomIcon size="large" aria-hidden />
      ) : (
        <PortalHelpIcon aria-hidden />
      );
    }
    return <HelpIcon aria-hidden />;
  }, [customIcon, isPortalsApplication]);

  return (
    <IconButton
      id="support-modal-link"
      data-testid={getTier1DataTestId('Help')}
      aria-label={messageService.get('HELP')}
      tooltipContent={messageService.get('HELP')}
      style={isMobileView ? { display: 'none' } : undefined}
      zIndex={10002}
      active={isHelpModalVisible}
      aria-expanded={isHelpModalVisible}
    >
      {helpIconComponent}
    </IconButton>
  );
}

Help.propTypes = {
  customIcon: PropTypes.element,
};
